import { createRoute } from '@hono/zod-openapi';
import type { ApiModule } from './modules.js';

/** Metadaten je Route ([V] 6.2, E-p1–p3); die Konventionsprüfung liegt in `conventions.ts`. */
export type RouteMeta = {
  module: ApiModule;
  /** camelCase; ergibt mit dem Modul die `operationId` `<modul>.<name>`. */
  name: string;
  /** `api` = unter `/api/v1` (Standard); `root` nur für `/healthz`, `/readyz`, `/auth/*`. */
  base?: 'api' | 'root';
  auth: 'session' | 'public';
  /** CSRF-Token nötig (alle mutierenden Sitzungsrouten). */
  csrf: boolean;
  /** `key` = Header `Idempotency-Key`; `domain` = Domänendienst dedupliziert die Wirkung (E-p3); `none`. */
  idempotency: 'key' | 'domain' | 'none';
  /** Nur Datenverantwortliche (Betroffenenrechte). */
  dataSteward?: boolean;
  /** Verhalten in der Demo-Sitzung ([V] E-o). */
  demo: 'allow' | 'read_only_forbidden';
};

type CreateRouteConfig = Parameters<typeof createRoute>[0];

/** Konfiguration einer Route ohne die aus den Metadaten abgeleiteten Felder. */
export type RouteInput = Omit<CreateRouteConfig, 'operationId' | 'tags'>;

type WithMeta<C extends RouteInput> = C & { operationId: string; tags: string[] };

/**
 * Route samt Metadaten. Der Typparameter trägt die konkrete Konfiguration (Pfad, Request, Antworten),
 * damit Handler ihre Typen aus der Routendefinition ableiten können; ohne Angabe passt jede Route
 * (`readonly ApiRoute[]`).
 */
export type ApiRoute<C extends RouteInput = RouteInput> = {
  meta: RouteMeta;
  route: ReturnType<typeof createRoute<C['path'], WithMeta<C>>>;
};

/** Legt eine Route an; `operationId` und `tags` folgen aus den Metadaten, alle Typen bleiben erhalten. */
export function defineRoute<const C extends RouteInput & { operationId?: never; tags?: never }>(
  meta: RouteMeta,
  config: C,
): ApiRoute<C> {
  return {
    meta,
    route: createRoute({
      ...config,
      operationId: `${meta.module}.${meta.name}`,
      tags: [meta.module],
    }),
  } as ApiRoute<C>;
}
